import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';

const C = {
  bg:            '#0B1928',
  bgCard:        '#132030',
  bgDeep:        '#091520',
  border:        'rgba(255,255,255,0.08)',
  orange:        '#FF6B00',
  orangeDim:     'rgba(255,107,0,0.14)',
  orangeBorder:  'rgba(255,107,0,0.38)',
  textPrimary:   '#F0F4F8',
  textSecondary: '#8496A8',
  textMuted:     '#4A6A8A',
  green:         '#00C851',
  greenDim:      'rgba(0,200,81,0.10)',
  greenBorder:   'rgba(0,200,81,0.28)',
  gold:          '#FFD700',
  goldDim:       'rgba(255,215,0,0.10)',
  goldBorder:    'rgba(255,215,0,0.35)',
};
const font = "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif";

// Badges required for The Bracketz 10 challenge (excludes Oracle + Legend)
const CHALLENGE_BADGES = [
  { key:'first_pick',      label:'First Pick',       pts:5,   category:'Participation', req:(s)=> s.total >= 1 },
  { key:'hat_trick',       label:'Hat Trick',         pts:10,  category:'Participation', req:(s)=> s.total >= 3 },
  { key:'on_fire',         label:'On Fire',            pts:15,  category:'Participation', req:(s)=> s.streak >= 5 },
  { key:'week_warrior',    label:'Week Warrior',       pts:25,  category:'Participation', req:(s)=> s.streak >= 7 },
  { key:'perfect_week',    label:'Perfect Week',       pts:30,  category:'Participation', req:(s)=> s.perfectWeek >= 1 },
  { key:'monthly',         label:'Monthly',            pts:40,  category:'Participation', req:(s)=> s.monthly >= 30 },
  { key:'century',         label:'Century',            pts:75,  category:'Participation', req:(s)=> s.total >= 100 },
  { key:'commentator',     label:'Commentator',        pts:5,   category:'Social',        req:(s)=> s.comments >= 1 },
  { key:'hot_take',        label:'Hot Take',            pts:20,  category:'Social',        req:(s)=> s.maxLikes >= 10 },
  { key:'debater',         label:'Debater',             pts:30,  category:'Social',        req:(s)=> s.comments >= 25 },
  { key:'with_people',     label:'With the People',    pts:15,  category:'Accuracy',      req:(s)=> s.crowdMatch >= 1 },
  { key:'contrarian',      label:'Contrarian',         pts:35,  category:'Accuracy',      req:(s)=> s.contrarian >= 10 },
  { key:'sports_fan',      label:'Sports Fan',         pts:20,  category:'Explorer',      req:(s)=> (s.byCategory['Sports']||0) >= 10 },
  { key:'cinephile',       label:'Cinephile',           pts:20,  category:'Explorer',      req:(s)=> (s.byCategory['Movies']||0) >= 5 },
  { key:'music_head',      label:'Music Head',          pts:20,  category:'Explorer',      req:(s)=> (s.byCategory['Music']||0) >= 5 },
  { key:'culture_vulture', label:'Culture Vulture',     pts:50,  category:'Explorer',      req:(s)=> s.categoriesPlayed >= 8 },
];
const TOTAL_CHALLENGE = CHALLENGE_BADGES.length; // 16

function getLocalDateString(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function PedestalSlot({ rank, winner }) {
  const medals = ['🥇','🥈','🥉'];
  const heights = [80, 65, 55, 48, 44, 40, 36, 34, 32, 30];
  const h = heights[rank - 1] || 30;

  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'6px', minWidth:'54px' }}>
      {/* Name plate */}
      <div style={{
        backgroundColor: winner ? C.goldDim : 'rgba(255,255,255,0.03)',
        border: '1px solid ' + (winner ? C.goldBorder : C.border),
        borderRadius:'8px', padding:'6px 4px', textAlign:'center', width:'100%',
        minHeight:'48px', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
      }}>
        {winner ? (
          <>
            <div style={{ fontSize:'16px', marginBottom:'2px' }}>{rank <= 3 ? medals[rank-1] : '🏅'}</div>
            <div style={{ fontSize:'8px', fontWeight:'700', color:C.gold, lineHeight:'1.2', wordBreak:'break-word' }}>
              {winner.username}
            </div>
            <div style={{ fontSize:'7px', color:'rgba(255,215,0,0.5)', marginTop:'2px' }}>
              {winner.completedDate}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize:'14px', opacity:0.3 }}>🔒</div>
            <div style={{ fontSize:'8px', color:C.textMuted, marginTop:'2px' }}>???</div>
          </>
        )}
      </div>

      {/* Pedestal */}
      <div style={{
        width:'100%', height: h + 'px',
        background: winner
          ? 'linear-gradient(180deg, rgba(255,215,0,0.3), rgba(255,215,0,0.08))'
          : 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
        border: '1px solid ' + (winner ? C.goldBorder : C.border),
        borderRadius:'4px 4px 0 0',
        display:'flex', alignItems:'flex-start', justifyContent:'center', paddingTop:'6px',
      }}>
        <span style={{ fontSize:'10px', fontWeight:'800', color: winner ? C.gold : C.textMuted }}>
          #{rank}
        </span>
      </div>
    </div>
  );
}

export default function HallOfFame({ user }) {
  const [loading,    setLoading]    = useState(true);
  const [winners,    setWinners]    = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [myProgress, setMyProgress] = useState(null);

  useEffect(() => { loadData(); }, []);// eslint-disable-line

  const loadData = async () => {
    try {
      // Load all user brackets + profiles to compute badge progress
      const allUBSnap  = await getDocs(query(collection(db,'userBrackets'), where('isSubmitted','==',true)));
      const allUBs     = allUBSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      const allBSnap   = await getDocs(collection(db,'brackets'));
      const allBrackets = allBSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      const profilesSnap = await getDocs(collection(db,'userProfiles'));
      const profiles   = {};
      profilesSnap.docs.forEach(d => { profiles[d.id] = d.data(); });
      const commentsSnap = await getDocs(collection(db,'comments'));
      const commentsByUser = {};
      commentsSnap.docs.forEach(d => {
        const uid = d.data().userId;
        if (uid) commentsByUser[uid] = (commentsByUser[uid]||0) + 1;
      });

      // Group UBs by user
      const byUser = {};
      allUBs.forEach(ub => {
        if (!byUser[ub.userId]) byUser[ub.userId] = [];
        byUser[ub.userId].push(ub);
      });

      const userScores = [];

      for (const [userId, ubs] of Object.entries(byUser)) {
        const profile   = profiles[userId] || {};
        const username  = profile.username || userId.slice(0,8);
        const submitted = ubs.filter(ub => ub.isSubmitted);
        const total     = submitted.length;

        // Streak
        const dates = new Set(submitted.map(ub => allBrackets.find(b => b.id === ub.bracketId)?.date).filter(Boolean));
        let streak = 0;
        const check = new Date();
        for (let i = 0; i < 365; i++) {
          const ds = getLocalDateString(check);
          if (dates.has(ds)) { streak++; check.setDate(check.getDate()-1); } else break;
        }

        // Monthly
        const now = new Date();
        const monthly = submitted.filter(ub => {
          const b = allBrackets.find(b => b.id === ub.bracketId);
          if (!b) return false;
          const d = new Date(b.date);
          return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
        }).length;

        // Perfect week
        const sorted = [...dates].sort();
        let perfectWeek = 0;
        for (let i = 0; i <= sorted.length - 7; i++) {
          let consecutive = 1;
          for (let j = i+1; j < sorted.length && consecutive < 7; j++) {
            const diff = (new Date(sorted[j]) - new Date(sorted[j-1])) / 86400000;
            if (diff === 1) consecutive++; else break;
          }
          if (consecutive >= 7) { perfectWeek = 1; break; }
        }

        // By category
        const byCategory = {};
        submitted.forEach(ub => {
          const b = allBrackets.find(b => b.id === ub.bracketId);
          if (b?.keyword) byCategory[b.keyword] = (byCategory[b.keyword]||0)+1;
        });
        const categoriesPlayed = Object.keys(byCategory).length;

        const stats = {
          total, streak, monthly, perfectWeek, byCategory, categoriesPlayed,
          comments: commentsByUser[userId] || 0,
          maxLikes: 0, contrarian: 0, crowdMatch: 0,
        };

        const earnedCount = CHALLENGE_BADGES.filter(b => b.req(stats)).length;
        const pct         = Math.round((earnedCount / TOTAL_CHALLENGE) * 100);
        const completed   = earnedCount === TOTAL_CHALLENGE;
        const completedDate = completed ? (profile.challengeCompletedDate || 'Recent') : null;

        userScores.push({ userId, username, earnedCount, pct, completed, completedDate, total, streak });
      }

      // Sort winners by completion date, then others by progress
      const completedUsers = userScores.filter(u => u.completed).sort((a,b) => (a.completedDate||'').localeCompare(b.completedDate||''));
      const inProgressUsers = userScores.filter(u => !u.completed).sort((a,b) => b.earnedCount - a.earnedCount || b.total - a.total);

      setWinners(completedUsers.slice(0, 10));
      setLeaderboard(inProgressUsers.slice(0, 15));

      // My progress
      const mine = userScores.find(u => u.userId === user.uid);
      setMyProgress(mine || { earnedCount:0, pct:0, completed:false });

    } catch(err) { console.error(err); }
    setLoading(false);
  };

  const filledSlots = winners.slice(0, 10);
  const emptySlots  = Array.from({ length: Math.max(0, 10 - filledSlots.length) }, (_, i) => i);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'400px', fontFamily:font }}>
      <div style={{ color:C.textMuted, fontSize:'13px' }}>Loading Hall of Fame...</div>
    </div>
  );

  return (
    <div style={{ padding:'16px', fontFamily:font, paddingBottom:'80px' }}>
      <style>{`
        @keyframes goldPulse { 0%,100%{box-shadow:0 0 8px rgba(255,215,0,0.2)} 50%{box-shadow:0 0 24px rgba(255,215,0,0.5)} }
        @keyframes shimmerGold { 0%{left:-100%} 100%{left:200%} }
      `}</style>

      {/* Header */}
      <div style={{ textAlign:'center', marginBottom:'24px' }}>
        <div style={{ fontSize:'32px', marginBottom:'8px' }}>🏆</div>
        <div style={{ fontSize:'22px', fontWeight:'800', color:C.gold }}>The Bracketz 10</div>
        <div style={{ fontSize:'12px', color:C.textMuted, marginTop:'4px', lineHeight:'1.6' }}>
          First 10 players to earn all 16 badges.{'\n'}Winners get Pro for life.
        </div>
      </div>

      {/* Challenge rules card */}
      <div style={{
        backgroundColor: C.goldDim, border:'1px solid ' + C.goldBorder,
        borderRadius:'14px', padding:'14px 16px', marginBottom:'20px',
        position:'relative', overflow:'hidden',
        animation: winners.length < 10 ? 'goldPulse 3s ease infinite' : 'none',
      }}>
        <div style={{ position:'absolute', top:0, left:'-100%', width:'60%', height:'100%',
          background:'linear-gradient(90deg, transparent, rgba(255,215,0,0.08), transparent)',
          animation:'shimmerGold 4s ease-in-out infinite', pointerEvents:'none' }}/>
        <div style={{ fontSize:'9px', fontWeight:'800', color:C.gold, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px' }}>
          The Challenge
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
          {[
            { label:'Badges required', value:'16 of 19' },
            { label:'Min. days to complete', value:'100+' },
            { label:'Spots remaining', value: Math.max(0, 10 - winners.length) + ' of 10' },
            { label:'Reward', value:'Pro for life 🎖' },
          ].map(({ label, value }) => (
            <div key={label} style={{ backgroundColor:'rgba(0,0,0,0.2)', borderRadius:'8px', padding:'8px 10px' }}>
              <div style={{ fontSize:'9px', color:'rgba(255,215,0,0.6)', marginBottom:'2px' }}>{label}</div>
              <div style={{ fontSize:'13px', fontWeight:'700', color:C.gold }}>{value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* My progress */}
      {myProgress && (
        <div style={{ backgroundColor:C.bgCard, border:'1px solid ' + C.orangeBorder, borderRadius:'12px', padding:'12px 14px', marginBottom:'20px' }}>
          <div style={{ fontSize:'9px', fontWeight:'700', color:C.orange, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'8px' }}>
            Your Progress
          </div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
            <span style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary }}>
              {myProgress.earnedCount} / {TOTAL_CHALLENGE} badges
            </span>
            <span style={{ fontSize:'13px', fontWeight:'800', color:myProgress.completed ? C.gold : C.orange }}>
              {myProgress.pct}%
            </span>
          </div>
          <div style={{ height:'6px', backgroundColor:'rgba(255,255,255,0.06)', borderRadius:'3px', overflow:'hidden' }}>
            <div style={{
              width: myProgress.pct + '%', height:'100%', borderRadius:'3px',
              background: myProgress.completed
                ? 'linear-gradient(90deg, ' + C.gold + ', #FFA500)'
                : 'linear-gradient(90deg, ' + C.orange + ', #FF9340)',
              transition:'width 0.5s ease',
            }}/>
          </div>
          {myProgress.completed && (
            <div style={{ marginTop:'8px', fontSize:'12px', color:C.gold, fontWeight:'700', textAlign:'center' }}>
              🎉 You completed The Challenge!
            </div>
          )}
        </div>
      )}

      {/* Pedestals — top 3 prominently, rest below */}
      <div style={{ fontSize:'9px', fontWeight:'700', color:C.textMuted, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'12px', textAlign:'center' }}>
        Hall of Fame
      </div>

      {/* Top 3 */}
      <div style={{ display:'flex', justifyContent:'center', alignItems:'flex-end', gap:'8px', marginBottom:'8px' }}>
        {[2,1,3].map(rank => {
          const winner = filledSlots[rank-1] || null;
          return <PedestalSlot key={rank} rank={rank} winner={winner} />;
        })}
      </div>

      {/* Positions 4-10 in a row */}
      <div style={{ display:'flex', justifyContent:'center', gap:'4px', marginBottom:'24px' }}>
        {[4,5,6,7,8,9,10].map(rank => {
          const winner = filledSlots[rank-1] || null;
          return <PedestalSlot key={rank} rank={rank} winner={winner} />;
        })}
      </div>

      {winners.length === 0 && (
        <div style={{ textAlign:'center', marginBottom:'24px', padding:'16px', backgroundColor:C.bgCard, borderRadius:'12px', border:'1px solid ' + C.border }}>
          <div style={{ fontSize:'24px', marginBottom:'8px' }}>👀</div>
          <div style={{ fontSize:'13px', fontWeight:'700', color:C.textPrimary, marginBottom:'4px' }}>No winners yet</div>
          <div style={{ fontSize:'11px', color:C.textMuted }}>Will you be the first?</div>
        </div>
      )}

      {/* Leaderboard — closest to completion */}
      {leaderboard.length > 0 && (
        <>
          <div style={{ fontSize:'9px', fontWeight:'700', color:C.textMuted, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'12px' }}>
            Closest to Completing
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            {leaderboard.map((entry, idx) => {
              const isMe = entry.userId === user.uid;
              return (
                <div key={entry.userId} style={{
                  backgroundColor: isMe ? C.orangeDim : C.bgCard,
                  border:'1px solid ' + (isMe ? C.orangeBorder : C.border),
                  borderRadius:'10px', padding:'10px 14px',
                  display:'flex', alignItems:'center', gap:'12px',
                }}>
                  <div style={{ width:'28px', textAlign:'center', flexShrink:0 }}>
                    <span style={{ fontSize:'12px', fontWeight:'700', color:C.textMuted }}>#{idx+1}</span>
                  </div>
                  <div style={{ width:'32px', height:'32px', borderRadius:'50%', flexShrink:0,
                    backgroundColor: isMe ? C.orangeDim : 'rgba(255,255,255,0.06)',
                    border:'1px solid ' + (isMe ? C.orangeBorder : C.border),
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:'13px', fontWeight:'700', color:isMe ? C.orange : C.textSecondary,
                  }}>
                    {entry.username.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'12px', fontWeight:'700', color:isMe ? C.textPrimary : C.textSecondary }}>
                      {entry.username}
                      {isMe && <span style={{ fontSize:'9px', color:C.orange, backgroundColor:C.orangeDim, padding:'1px 6px', borderRadius:'6px', marginLeft:'6px' }}>YOU</span>}
                    </div>
                    <div style={{ height:'3px', backgroundColor:'rgba(255,255,255,0.06)', borderRadius:'2px', overflow:'hidden', marginTop:'4px' }}>
                      <div style={{ width:entry.pct+'%', height:'100%', backgroundColor:isMe?C.orange:C.textMuted, borderRadius:'2px' }}/>
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:'12px', fontWeight:'800', color:isMe?C.orange:C.textPrimary }}>{entry.earnedCount}/{TOTAL_CHALLENGE}</div>
                    <div style={{ fontSize:'9px', color:C.textMuted }}>{entry.pct}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Required badges list */}
      <div style={{ marginTop:'24px', fontSize:'9px', fontWeight:'700', color:C.textMuted, letterSpacing:'2px', textTransform:'uppercase', marginBottom:'12px' }}>
        Required Badges
      </div>
      <div style={{ backgroundColor:C.bgCard, borderRadius:'12px', border:'1px solid ' + C.border, padding:'12px 14px' }}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
          {CHALLENGE_BADGES.map(b => (
            <div key={b.key} style={{
              fontSize:'9px', fontWeight:'600', padding:'3px 8px', borderRadius:'10px',
              backgroundColor: C.bgDeep || '#091520',
              border:'0.5px solid ' + C.border, color:C.textMuted,
            }}>
              {b.label}
            </div>
          ))}
        </div>
        <div style={{ fontSize:'10px', color:C.textMuted, marginTop:'10px', lineHeight:'1.5' }}>
          Oracle and Legend badges are excluded. Century badge (100 brackets) is the minimum gate — expect 100+ days of daily play to complete the challenge.
        </div>
      </div>
    </div>
  );
}
